// ============================================================
// ELC Shift — Uploads / AI Rules + Custom Rules (v96.0)
//
// AI rota generator rules engine. Manages two layers of rules:
//   1. SCOPED RULES — cascading business → country → city → store
//      rules. Numeric fields (maxHours, lunchStagger etc.), shift
//      templates, day-toggle sets (peakDays, quietDays, weekends).
//      Stored per-scope in localStorage; resolved by cascading
//      override at AI rota generation time.
//   2. CUSTOM RULES — free-form named rules (text + scope). Stored
//      centrally in localStorage. Each rule can be scoped to
//      business/country/city/store independently. Toggleable
//      active flag per rule.
//
// Public surface (callable from admin.html inline handlers):
//   Uploads.rules.switchAiSubTab(tab)         — Rules vs Generate tab toggle
//   Uploads.rules.switchRuleScope(scope)      — business/country/city/store
//   Uploads.rules._scopeLabel(scope)          — display label for scope
//   Uploads.rules.loadRulesForScope(scope)    — load rules from localStorage
//   Uploads.rules.saveAllRules()              — save all rules + custom rules
//   Uploads.rules.resolveRulesForStore(store) — cascade-resolve for a store
//   Uploads.rules.renderRulesForScope()       — render the form area for scope
//   Uploads.rules.loadAllCustomRules()        — load all custom rules
//   Uploads.rules.saveAllCustomRules(rules)   — save all custom rules
//   Uploads.rules._customRuleScopeLabel(rule) — display label for custom rule scope
//   Uploads.rules._readRulesFromForm()        — read current form state
//   Uploads.rules.addCustomRule()             — append blank rule + re-render
//   Uploads.rules.toggleCustomRule(idx)       — toggle active flag
//   Uploads.rules.deleteCustomRule(idx)       — confirm + remove
//
// State shared with inline admin.html code (declared as `var` in
// admin.html so it lives on `window` — bare-identifier access from
// both module + inline resolves to the same backing storage):
//   - _currentRuleScope  (read+written by switchRuleScope; read by
//                         saveAllRules, renderRulesForScope, and by
//                         inline renderRulesPreview)
//
// Free globals (admin.html lexical scope):
//   - ALL_STORES              (canonical store list for dropdown)
//   - DEFAULT_TRADING         (default values for scoped rules)
//   - DAYS_LIST               (day-toggle list, used by buildDayTogglesIn)
//   - showToast               (toast helper)
//   - DB                      (db.js facade; DB.config.set for cross-device sync)
//   - buildDayTogglesIn       (admin.html helper, used by renderRulesForScope)
//   - addShiftTemplateToForm  (admin.html helper)
//   - safeTimeInputValue      (admin.html helper)
//   - renderRulesPreview      (admin.html preview screen, called when rules change)
// ============================================================

// Module-internal helpers (NOT exported — only used inside this module)

function _ruleKey(scope) {
  if (scope === 'business') return 'elc_trading_pattern';
  if (scope === 'country') return 'elc_rules_country_' + (document.getElementById('ruleScopeCountry')?.value || 'ELC - UAE');
  if (scope === 'city')    return 'elc_rules_city_' + (document.getElementById('ruleScopeCity')?.value || 'Dubai');
  if (scope === 'store')   return 'elc_rules_store_' + (document.getElementById('ruleScopeStore')?.value || '');
  return 'elc_trading_pattern';
}

function _parseScopeSelect(val) {
  if (!val || val === 'business') return {scope:'business', scopeValue:''};
  const [type, ...rest] = val.split(':');
  return {scope: type, scopeValue: rest.join(':')};
}

// ── Public exports ──────────────────────────────────────────

import { QATAR_WEEKEND, weekendFor } from '../shared/rota-country.js';   // v133.94: one Qatar weekend
export function switchAiSubTab(tab) {
  document.getElementById('aiPanelRules').style.display    = tab === 'rules'    ? 'block' : 'none';
  document.getElementById('aiPanelGenerate').style.display = tab === 'generate' ? 'block' : 'none';
  document.getElementById('aiSubTabRules').className    = tab === 'rules'    ? 'btn btn-green' : 'btn btn-outline';
  document.getElementById('aiSubTabGenerate').className = tab === 'generate' ? 'btn btn-green' : 'btn btn-outline';
  if (tab === 'rules') {
    // Populate store dropdown in scope selector
    const sel = document.getElementById('ruleScopeStore');
    if (sel && sel.options.length <= 0) {
      ALL_STORES.filter(s => s !== 'Retail Relief Managers').forEach(s => {
        const o = document.createElement('option'); o.value = s; o.textContent = s; sel.appendChild(o);
      });
    }
    switchRuleScope(_currentRuleScope);
  }
}

export function switchRuleScope(scope) {
  _currentRuleScope = scope;
  ['Business','Country','City','Store'].forEach(s => {
    const btn = document.getElementById('rs' + s);
    if (btn) btn.className = 'rule-scope-btn' + (scope === s.toLowerCase() ? ' active' : '');
  });
  const sel = document.getElementById('ruleScopeSelector');
  sel.style.display = scope === 'business' ? 'none' : 'flex';
  sel.style.gap = '8px';
  document.getElementById('ruleScopeCountry').style.display = scope === 'country' ? 'block' : 'none';
  document.getElementById('ruleScopeCity').style.display    = scope === 'city'    ? 'block' : 'none';
  document.getElementById('ruleScopeStore').style.display   = scope === 'store'   ? 'block' : 'none';
  renderRulesForScope();
}

export function _scopeLabel(scope) {
  if (scope === 'business') return 'Business-wide (all stores)';
  if (scope === 'country') return (document.getElementById('ruleScopeCountry')?.value || 'UAE') + ' — country rules';
  if (scope === 'city')    return (document.getElementById('ruleScopeCity')?.value || 'Dubai') + ' — city rules';
  if (scope === 'store')   return (document.getElementById('ruleScopeStore')?.value || '') + ' — store rules';
  return '';
}

export function loadRulesForScope(scope) {
  const key = _ruleKey(scope);
  const base = scope === 'business' ? DEFAULT_TRADING : {};
  try { return Object.assign({}, base, JSON.parse(localStorage.getItem(key) || '{}')); }
  catch { return Object.assign({}, base); }
}

export function saveAllRules() {
  const scope = _currentRuleScope;
  const key = _ruleKey(scope);
  const form = document.getElementById('rulesFormArea');

  const rules = {};

  // Shift templates (business scope only)
  if (scope === 'business') {
    const templates = [];
    form.querySelectorAll('.shift-template-row').forEach(row => {
      const label = row.querySelector('.st-label')?.value.trim();
      const start = row.querySelector('.st-start')?.value;
      const finish = row.querySelector('.st-finish')?.value;
      const type  = row.querySelector('.st-type')?.value;
      if (label && start && finish) templates.push({label, start, finish, type});
    });
    if (templates.length) rules.shiftTemplates = templates;
  }

  // Numeric fields
  ['maxHours','lunchStagger','lunchDuration','minFloor','closeCoverCount','morningSplit'].forEach(id => {
    const el = form.querySelector('#r_'+id);
    if (el && el.value) rules[id] = parseInt(el.value);
  });

  // Day toggles
  ['allowedDaysOff','peakDays','quietDays','uaeWeekend','qatarWeekend'].forEach(id => {
    const container = form.querySelector('#r_'+id);
    if (container) rules[id] = [...container.querySelectorAll('.day-toggle.active')].map(el => el.dataset.day);
  });

  // Text fields
  ['fridayQatar'].forEach(id => {
    const el = form.querySelector('#r_'+id);
    if (el) rules[id] = el.value.trim();
  });

  // Custom standalone rules — save from form to central store
  saveAllCustomRules(_readRulesFromForm());

  // Remove empty arrays (means "use parent scope")
  Object.keys(rules).forEach(k => {
    if (Array.isArray(rules[k]) && rules[k].length === 0) delete rules[k];
  });

  if (scope === 'business') {
    // Always keep all fields for business
    localStorage.setItem(key, JSON.stringify(Object.assign({}, DEFAULT_TRADING, rules)));
  } else {
    // For scoped rules, only save what's actually set (overrides parent)
    const existing = loadRulesForScope(scope);
    localStorage.setItem(key, JSON.stringify(Object.assign(existing, rules)));
  }

  showToast('Rules saved for ' + _scopeLabel(scope), 'success');
  renderRulesPreview();
  // Also keep legacy key in sync for AI generator
  if (scope === 'business') {
    localStorage.setItem('elc_trading_pattern', localStorage.getItem(key));
    // Sync to DB for cross-device (index.html reads from this)
    try {
      const tp = JSON.parse(localStorage.getItem(key) || '{}');
      DB.config.set('trading_pattern', tp).catch(e => console.warn('[DB] Trading pattern save failed:', e.message));
    } catch(e) {}
  }
}

export function resolveRulesForStore(storeName) {
  const business = loadRulesForScope('business');
  const country = storeName.startsWith('Doha') ? 'ELC - Qatar' : 'ELC - UAE';
  const city = storeName.split(' - ')[0] || '';
  const countryKey = 'elc_rules_country_' + country;
  const cityKey = 'elc_rules_city_' + city;
  const storeKey = 'elc_rules_store_' + storeName;

  let rules = Object.assign({}, business);
  try { Object.assign(rules, JSON.parse(localStorage.getItem(countryKey) || '{}')); } catch {}
  try { Object.assign(rules, JSON.parse(localStorage.getItem(cityKey) || '{}')); } catch {}
  try { Object.assign(rules, JSON.parse(localStorage.getItem(storeKey) || '{}')); } catch {}
  return rules;
}

export function renderRulesForScope() {
  const scope = _currentRuleScope;
  const rules = loadRulesForScope(scope);
  const isGlobal = scope === 'business';
  const form = document.getElementById('rulesFormArea');

  // Build form HTML with prefixed IDs so they don't clash with old trading page
  let html = '';

  // ── SHIFT TEMPLATES (business scope only) ──
  if (isGlobal) {
    html += `<div class="card"><div class="card-title">Shift Templates</div>
    <div style="font-size:13px;color:var(--sub);margin-bottom:12px">Define the shift building blocks the AI uses. Add or edit as your trading pattern changes.</div>
    <div id="r_shiftTemplates">`;
    (rules.shiftTemplates || DEFAULT_TRADING.shiftTemplates).forEach((t, i) => {
      html += `<div class="shift-template-row" style="display:flex;gap:8px;align-items:center;margin-bottom:8px;flex-wrap:wrap">
        <input class="form-input st-label" value="${t.label}" placeholder="Label" style="width:90px">
        <input type="time" class="form-input st-start" value="${safeTimeInputValue(t.start)}" style="width:100px">
        <span style="color:var(--sub);font-size:12px">to</span>
        <input type="time" class="form-input st-finish" value="${safeTimeInputValue(t.finish)}" style="width:100px">
        <select class="form-input st-type" style="width:100px">
          <option value="early" ${t.type==='early'?'selected':''}>Early</option>
          <option value="mid"   ${t.type==='mid'?'selected':''}>Mid</option>
          <option value="late"  ${t.type==='late'?'selected':''}>Late</option>
        </select>
        <button onclick="this.closest('.shift-template-row').remove()" style="background:none;border:none;color:var(--red);cursor:pointer;font-size:16px">✕</button>
      </div>`;
    });
    html += `</div>
    <button class="btn btn-outline btn-sm" onclick="addShiftTemplateToForm()" style="margin-top:8px">+ Add Shift Template</button>
    </div>`;
  }

  // ── SCOPE LABEL ──
  if (!isGlobal) {
    html += `<div style="background:var(--adm-warn-pale);border:1px solid var(--adm-warn-line);border-radius:10px;padding:10px 14px;margin-bottom:14px;font-size:12px;color:var(--warn-deep)">
      <strong>Scope:</strong> ${_scopeLabel(scope)} — rules set here <strong>override</strong> the business-wide defaults. Leave fields blank to inherit from the parent scope.
    </div>`;
  }

  // ── SCHEDULING RULES ──
  html += `<div class="card"><div class="card-title">Scheduling Rules</div><div class="form-grid">
    <div class="form-group">
      <div class="form-label">Max hours/week per person</div>
      <div style="display:flex;align-items:center;gap:8px">
        <input type="number" class="form-input" id="r_maxHours" min="1" max="72" value="${rules.maxHours||48}" style="width:80px">
        <span style="font-size:13px;color:var(--sub)">hours</span>
      </div>
      <div class="form-note">Standard: 48h. Ramadan: 36h.</div>
    </div>
    <div class="form-group">
      <div class="form-label">Lunch stagger interval</div>
      <div style="display:flex;align-items:center;gap:8px">
        <input type="number" class="form-input" id="r_lunchStagger" min="15" max="60" step="15" value="${rules.lunchStagger||30}" style="width:80px">
        <span style="font-size:13px;color:var(--sub)">minutes</span>
      </div>
    </div>
    <div class="form-group">
      <div class="form-label">Lunch break duration</div>
      <div style="display:flex;align-items:center;gap:8px">
        <input type="number" class="form-input" id="r_lunchDuration" min="30" max="90" step="15" value="${rules.lunchDuration||60}" style="width:80px">
        <span style="font-size:13px;color:var(--sub)">minutes</span>
      </div>
    </div>
    <div class="form-group">
      <div class="form-label">Minimum staff on floor</div>
      <div style="display:flex;align-items:center;gap:8px">
        <input type="number" class="form-input" id="r_minFloor" min="1" max="10" value="${rules.minFloor||1}" style="width:80px">
        <span style="font-size:13px;color:var(--sub)">person(s)</span>
      </div>
    </div>
    <div class="form-group full">
      <div class="form-label">Close cover — minimum per day</div>
      <div style="display:flex;align-items:center;gap:8px">
        <input type="number" class="form-input" id="r_closeCoverCount" min="1" max="5" value="${rules.closeCoverCount||1}" style="width:60px">
        <span style="font-size:13px;color:var(--sub)">person(s) must work until close every day</span>
      </div>
    </div>
    <div class="form-group full">
      <div class="form-label">Days off permitted on</div>
      <div style="display:flex;flex-wrap:wrap;gap:6px;margin-top:6px" id="r_allowedDaysOff"></div>
      <div class="form-note">Unchecked days = D/O never scheduled on that day</div>
    </div>
  </div></div>`;

  // ── TRADING PATTERN ──
  html += `<div class="card"><div class="card-title">Trading Pattern</div><div class="form-grid">
    <div class="form-group full">
      <div class="form-label">Sales split — Morning (09:00–17:00) vs Evening (17:00–close)</div>
      <div style="display:flex;align-items:center;gap:12px;flex-wrap:wrap">
        <div style="display:flex;align-items:center;gap:6px">
          <span style="font-size:13px;color:var(--sub)">Morning</span>
          <input type="number" class="form-input" id="r_morningSplit" min="0" max="100" value="${rules.morningSplit||50}" style="width:72px;text-align:center"
            oninput="document.getElementById('r_eveningSplit').textContent=(100-parseInt(this.value||50))+'%'">
          <span style="font-size:13px;color:var(--sub)">%</span>
        </div>
        <span>|</span>
        <div style="display:flex;align-items:center;gap:6px">
          <span style="font-size:13px;color:var(--sub)">Evening</span>
          <span id="r_eveningSplit" style="font-size:15px;font-weight:700;color:var(--green)">${100-(rules.morningSplit||50)}%</span>
        </div>
      </div>
    </div>
    <div class="form-group">
      <div class="form-label">Peak days (maximise late shifts)</div>
      <div style="display:flex;flex-wrap:wrap;gap:6px;margin-top:6px" id="r_peakDays"></div>
    </div>
    <div class="form-group">
      <div class="form-label">Quietest days (prefer for D/O)</div>
      <div style="display:flex;flex-wrap:wrap;gap:6px;margin-top:6px" id="r_quietDays"></div>
    </div>
  </div></div>`;

  // ── REGIONAL SETTINGS ──
  html += `<div class="card"><div class="card-title">Regional Settings</div><div class="form-grid">
    <div class="form-group">
      <div class="form-label">🇦🇪 UAE weekend days (no D/O)</div>
      <div style="display:flex;flex-wrap:wrap;gap:6px;margin-top:6px" id="r_uaeWeekend"></div>
    </div>
    <div class="form-group">
      <div class="form-label">🇶🇦 Qatar weekend days (no D/O)</div>
      <div style="display:flex;flex-wrap:wrap;gap:6px;margin-top:6px" id="r_qatarWeekend"></div>
    </div>
    <div class="form-group full">
      <div class="form-label">Qatar Friday note for AI</div>
      <input type="text" class="form-input" id="r_fridayQatar" value="${rules.fridayQatar||''}" placeholder="e.g. Friday morning quiet until prayer (~13:30)">
    </div>
  </div></div>`;

  // ── RULES LIBRARY (global — shows all rules regardless of scope view) ──
  const allRules = loadAllCustomRules();
  html += `<div class="card">
    <div style="display:flex;align-items:center;justify-content:space-between;margin-bottom:4px">
      <div>
        <div class="card-title" style="margin-bottom:2px">Rules Library</div>
        <div style="font-size:12px;color:var(--sub);line-height:1.5">All rules across all scopes. Each rule has its own scope — toggle off to suspend without deleting.</div>
      </div>
      <button class="btn btn-green btn-sm" onclick="Uploads.rules.addCustomRule()">+ Add Rule</button>
    </div>
    <div id="customRulesList" style="margin-top:12px">`;

  if (allRules.length === 0) {
    html += `<div id="customRulesEmpty" style="text-align:center;padding:28px;color:var(--sub);font-size:13px;border:2px dashed var(--border);border-radius:10px">
      No rules yet — tap <strong>+ Add Rule</strong> to create one.<br>
      <span style="font-size:11px">Example: "Qatar stores close at 13:00 on Fridays for prayers and reopen at 14:30"</span>
    </div>`;
  } else {
    allRules.forEach((rule, i) => {
      const isActive = rule.active !== false;
      const scopeLabel = _customRuleScopeLabel(rule);
      html += `<div class="custom-rule-row" data-idx="${i}" style="display:flex;gap:10px;align-items:flex-start;padding:12px;border:1px solid var(--border);border-radius:10px;margin-bottom:8px;background:${isActive?'var(--card)':'var(--surface-2)'}">
        <button onclick="Uploads.rules.toggleCustomRule(${i})" data-active="${isActive}" style="flex-shrink:0;margin-top:3px;width:36px;height:20px;border-radius:10px;border:none;cursor:pointer;background:${isActive?'var(--green)':'var(--border-strong)'};position:relative;transition:all .2s">
          <span style="position:absolute;top:2px;left:${isActive?'18px':'2px'};width:16px;height:16px;border-radius:50%;background:#fff;transition:left .2s;display:block"></span>
        </button>
        <div style="flex:1;min-width:0">
          <div style="display:flex;gap:8px;align-items:center;margin-bottom:6px;flex-wrap:wrap">
            <input class="form-input rule-name" value="${rule.name||''}" placeholder="Rule name" style="font-weight:600;font-size:13px;flex:1;min-width:140px;padding:4px 8px">
            <select class="rule-scope-select" onchange="Uploads.rules.renderRulesPreview()" style="border:1.5px solid var(--border);border-radius:8px;padding:4px 8px;font-size:11px;font-weight:600;font-family:inherit;background:var(--surface-2);cursor:pointer">
              <option value="business" ${rule.scope==='business'?'selected':''}>🌍 All Business</option>
              <optgroup label="By Country">
                <option value="country:ELC - UAE"      ${rule.scope==='country'&&rule.scopeValue==='ELC - UAE'?'selected':''}>🇦🇪 UAE</option>
                <option value="country:ELC - Qatar"    ${rule.scope==='country'&&rule.scopeValue==='ELC - Qatar'?'selected':''}>🇶🇦 Qatar</option>
                <option value="country:ELC - KSA"      ${rule.scope==='country'&&rule.scopeValue==='ELC - KSA'?'selected':''}>🇸🇦 KSA</option>
                <option value="country:ELC - Kuwait"   ${rule.scope==='country'&&rule.scopeValue==='ELC - Kuwait'?'selected':''}>🇰🇼 Kuwait</option>
                <option value="country:ELC - Bahrain"  ${rule.scope==='country'&&rule.scopeValue==='ELC - Bahrain'?'selected':''}>🇧🇭 Bahrain</option>
              </optgroup>
              <optgroup label="By City">
                <option value="city:Dubai"      ${rule.scope==='city'&&rule.scopeValue==='Dubai'?'selected':''}>Dubai</option>
                <option value="city:Abu Dhabi"  ${rule.scope==='city'&&rule.scopeValue==='Abu Dhabi'?'selected':''}>Abu Dhabi</option>
                <option value="city:Al Ain"     ${rule.scope==='city'&&rule.scopeValue==='Al Ain'?'selected':''}>Al Ain</option>
                <option value="city:RAK"        ${rule.scope==='city'&&rule.scopeValue==='RAK'?'selected':''}>Ras Al Khaimah</option>
                <option value="city:Doha"       ${rule.scope==='city'&&rule.scopeValue==='Doha'?'selected':''}>Doha</option>
              </optgroup>
              <optgroup label="By Store">
                ${ALL_STORES.filter(s=>s!=='Retail Relief Managers').map(s=>`<option value="store:${s}" ${rule.scope==='store'&&rule.scopeValue===s?'selected':''}>${s.split(' - ').pop()}</option>`).join('')}
              </optgroup>
            </select>
            <button onclick="Uploads.rules.deleteCustomRule(${i})" style="background:none;border:none;color:var(--border-strong);cursor:pointer;font-size:18px;flex-shrink:0;padding:0 2px" title="Delete rule">🗑</button>
          </div>
          <textarea class="form-input rule-text" rows="2" oninput="Uploads.rules.renderRulesPreview()" placeholder="Write the rule exactly as you want the AI to receive it..." style="font-size:12px;line-height:1.5;resize:vertical;width:100%;color:${isActive?'inherit':'var(--sub)'}">${rule.text||''}</textarea>
          <div style="display:flex;align-items:center;gap:6px;margin-top:4px">
            <span style="font-size:10px;color:var(--sub)">Applies to: <strong>${scopeLabel}</strong></span>
            ${!isActive?'<span style="font-size:10px;color:var(--sub)">· ⏸ Suspended</span>':''}
          </div>
        </div>
      </div>`;
    });
  }

  html += `</div></div>`;

  form.innerHTML = html;

  // Build day toggles
  buildDayTogglesIn('r_allowedDaysOff', rules.allowedDaysOff || DEFAULT_TRADING.allowedDaysOff, form);
  buildDayTogglesIn('r_peakDays',       rules.peakDays       || DEFAULT_TRADING.peakDays,       form);
  buildDayTogglesIn('r_quietDays',      rules.quietDays      || DEFAULT_TRADING.quietDays,      form);
  buildDayTogglesIn('r_uaeWeekend',     rules.uaeWeekend     || DEFAULT_TRADING.uaeWeekend,     form);
  buildDayTogglesIn('r_qatarWeekend',   rules.qatarWeekend   || DEFAULT_TRADING.qatarWeekend,   form);

  renderRulesPreview();
}

export function loadAllCustomRules() {
  try { return JSON.parse(localStorage.getItem('elc_custom_rules') || '[]'); }
  catch { return []; }
}

export function saveAllCustomRules(rules) {
  localStorage.setItem('elc_custom_rules', JSON.stringify(rules));
}

export function _customRuleScopeLabel(rule) {
  if (!rule.scope || rule.scope === 'business') return 'All stores (business-wide)';
  if (rule.scope === 'country') return rule.scopeValue || 'Country';
  if (rule.scope === 'city')    return rule.scopeValue + ' (city)' || 'City';
  if (rule.scope === 'store')   return (rule.scopeValue||'').split(' - ').pop() + ' (store)';
  return rule.scope;
}

export function _readRulesFromForm() {
  const rules = [];
  document.querySelectorAll('.custom-rule-row').forEach(row => {
    const btn      = row.querySelector('button[data-active]');
    const active   = !btn || btn.dataset.active !== 'false';
    const name     = row.querySelector('.rule-name')?.value.trim() || '';
    const text     = row.querySelector('.rule-text')?.value.trim() || '';
    const scopeRaw = row.querySelector('.rule-scope-select')?.value || 'business';
    const {scope, scopeValue} = _parseScopeSelect(scopeRaw);
    if (name || text) rules.push({name, text, active, scope, scopeValue});
  });
  return rules;
}

export function addCustomRule() {
  // Save current form state first
  saveAllCustomRules(_readRulesFromForm());
  // Add a new blank rule
  const all = loadAllCustomRules();
  all.push({name:'', text:'', active:true, scope:'business', scopeValue:''});
  saveAllCustomRules(all);
  // Re-render the rules form to show the new row
  renderRulesForScope();
  // Focus the last name input
  const rows = document.querySelectorAll('.custom-rule-row');
  if (rows.length) rows[rows.length-1].querySelector('.rule-name')?.focus();
}

export function toggleCustomRule(idx) {
  const rules = _readRulesFromForm();
  if (rules[idx]) {
    rules[idx].active = !rules[idx].active;
    saveAllCustomRules(rules);
  }
  renderRulesForScope();
}

export async function deleteCustomRule(idx) {
  if (!(await (window.uiConfirm || function (m) { return Promise.resolve(confirm(m)); })('Delete this rule?', { danger: true, confirmLabel: 'Delete' }))) return;
  const rules = _readRulesFromForm();
  rules.splice(idx, 1);
  saveAllCustomRules(rules);
  renderRulesForScope();
}


// ════════════════════════════════════════════════════════════
// v97.0 additions
// ════════════════════════════════════════════════════════════
// renderRulesPreview moved into this module — it CONSUMES rules state
// heavily (calls loadRulesForScope, loadAllCustomRules, _scopeLabel,
// _readRulesFromForm, _customRuleScopeLabel). Pre-v97 it was inline in
// admin.html with bidirectional free-global references that resolved at
// runtime. v97 makes the dependency one-way: this module owns both the
// state AND its preview renderer.
//
// DEFAULT_TRADING moved into this module — it's the canonical default
// trading-pattern config used by loadRulesForScope (fallback), saveAllRules
// (preserves all fields for business scope), renderRulesPreview (fallback
// for shiftTemplates), AND by Uploads.trading.load + buildContext. Putting
// it here lets Uploads.trading import it as a sibling.

export const DEFAULT_TRADING = {
  // Shift templates
  shiftTemplates: [
    { label: 'Early',    start: '09:00', finish: '18:00', type: 'early' },
    { label: 'Mid',      start: '13:00', finish: '22:00', type: 'mid'   },
    { label: 'Late',     start: '15:00', finish: '24:00', type: 'late'  },
    { label: 'Extended', start: '14:00', finish: '23:00', type: 'mid'   },
  ],
  // Scheduling rules
  maxHours:        48,
  lunchStagger:    30,
  lunchDuration:   60,
  minFloor:        1,
  closeCoverCount: 1,
  allowedDaysOff:  ['Monday','Tuesday','Wednesday','Thursday'],
  // Trading pattern
  morningSplit:    50,
  peakDays:        ['Thursday','Friday','Saturday','Sunday'],
  quietDays:       ['Monday','Tuesday'],
  // Regional
  uaeWeekend:      ['Saturday','Sunday'],
  qatarWeekend:    QATAR_WEEKEND,   // v133.94: Friday and Saturday (was Thursday and Friday)
  fridayQatar:     'Friday morning in Qatar is quiet until after prayer time (~13:30). Avoid scheduling multiple early shifts on Friday in Qatar stores.',
  otherNotes:      '',
};

export function renderRulesPreview() {
  const scope = _currentRuleScope;
  const rules = loadRulesForScope(scope);
  const label = _scopeLabel(scope);

  // For non-business scopes, show the cascaded result
  let cascaded = rules;
  if (scope !== 'business') {
    // Quick cascade: business + this scope
    cascaded = Object.assign({}, DEFAULT_TRADING, loadRulesForScope('business'), rules);
  }

  const shifts = (cascaded.shiftTemplates || DEFAULT_TRADING.shiftTemplates)
    .map(t => `  ${t.label}: ${t.start}–${t.finish} (${t.type})`).join('\n');

  // Collect active custom rules relevant to current scope view
  const allCustom = loadAllCustomRules().filter(r => r.active !== false);
  // Also include any unsaved in-form changes
  const formRules = _readRulesFromForm().filter(r => r.active !== false && r.text);
  // Use form rules (most current) instead of saved for preview
  const activeRules = formRules;

  const customSection = activeRules.length
    ? '\n\nSTANDALONE RULES:\n' + activeRules.map((r,i) =>
        `  ${i+1}. [${_customRuleScopeLabel(r)}]${r.name?' '+r.name+':':''} ${r.text}`
      ).join('\n')
    : '';

  const preview = `=== ${label} ===

SHIFT TEMPLATES:
${shifts}

SCHEDULING RULES:
  Max hours/week: ${cascaded.maxHours || 48}h
  Lunch stagger: ${cascaded.lunchStagger || 30}min | Duration: ${cascaded.lunchDuration || 60}min
  Minimum floor: ${cascaded.minFloor || 1} | Close cover: ${cascaded.closeCoverCount || 1} person(s)/day
  D/O permitted: ${(cascaded.allowedDaysOff || []).join(', ') || 'not set'}

TRADING PATTERN:
  Morning/Evening split: ${cascaded.morningSplit || 50}% / ${100-(cascaded.morningSplit||50)}%
  Peak days: ${(cascaded.peakDays || []).join(', ') || 'none'}
  Quiet days: ${(cascaded.quietDays || []).join(', ') || 'none'}

REGIONAL:
  UAE weekend: ${(cascaded.uaeWeekend || []).join(', ')}
  Qatar weekend: ${weekendFor(true, cascaded).join(', ')}${cascaded.fridayQatar ? '\n  Qatar Friday: ' + cascaded.fridayQatar : ''}${customSection}`;

  const el = document.getElementById('tpRulesPreview');
  if (el) el.textContent = preview;
}
